import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const LS_KEY = "pd-nav-prefs";
const EVT = "pd-nav-prefs-changed";

// Per-user nav layout: { more: [paths under "Mehr"], order: [paths] } or null = default.
export function useNavPrefs() {
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)); } catch (_) { return null; }
  });
  useEffect(() => {
    api.get("/tasks/nav-prefs").then((r) => {
      const p = Array.isArray(r.data?.more) ? r.data : null;
      setPrefs(p);
      try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch (_) {}
    }).catch(() => {});
    const onChange = (e) => setPrefs(e.detail);
    window.addEventListener(EVT, onChange);
    return () => window.removeEventListener(EVT, onChange);
  }, []);
  return prefs;
}

// Sort nav items by the saved order (unknown/new items keep their default place at the end).
export function sortNav(items, order) {
  if (!order || !order.length) return items;
  const pos = (n) => { const i = order.indexOf(n.to); return i === -1 ? 1000 + items.indexOf(n) : i; };
  return [...items].sort((a, b) => pos(a) - pos(b));
}

async function savePrefs(prefs) {
  await api.put("/tasks/nav-prefs", prefs || {});
  try { localStorage.setItem(LS_KEY, JSON.stringify(prefs)); } catch (_) {}
  window.dispatchEvent(new CustomEvent(EVT, { detail: prefs }));
}

function Row({ n, i, last, on, onToggle, onMove }) {
  const slug = n.to.replace(/\//g, "-").slice(1);
  return (
    <li className="flex items-center gap-2 py-2" data-testid={`nav-setting-row-${slug}`}>
      <div className="flex flex-col">
        <button type="button" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`nav-setting-up-${slug}`}
          className="p-0.5 rounded text-slate-500 hover:bg-slate-100 hover:text-primary disabled:opacity-25 transition-colors" title="Nach oben">
          <ChevronUp className="h-4 w-4" />
        </button>
        <button type="button" disabled={last} onClick={() => onMove(i, 1)} data-testid={`nav-setting-down-${slug}`}
          className="p-0.5 rounded text-slate-500 hover:bg-slate-100 hover:text-primary disabled:opacity-25 transition-colors" title="Nach unten">
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
      <span className="flex-1 flex items-center gap-2 text-sm font-medium"><n.icon className="h-4 w-4 text-slate-500" />{n.label}</span>
      <span className="flex items-center gap-2 text-xs text-slate-500">
        {on ? "Leiste" : "Mehr"}
        <Switch checked={on} onCheckedChange={onToggle} data-testid={`nav-setting-${slug}`} />
      </span>
    </li>
  );
}

// Dialog: order the menu items and choose "in der Leiste" (on) or "unter Mehr" (off).
export default function NavSettingsDialog({ open, onOpenChange, items, isMore }) {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setList(items); setSel(Object.fromEntries(items.map((n) => [n.to, !isMore(n)]))); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (i, d) => setList((l) => { const c = [...l]; [c[i], c[i + d]] = [c[i + d], c[i]]; return c; });
  const save = async (prefs) => {
    setBusy(true);
    try { await savePrefs(prefs); toast.success("Menü gespeichert"); onOpenChange(false); }
    catch (_) { toast.error("Speichern nicht möglich – bitte mit Internetverbindung erneut versuchen"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="nav-settings-dialog">
        <DialogHeader>
          <DialogTitle>Menü einstellen</DialogTitle>
          <DialogDescription>Mit den Pfeilen die Reihenfolge ändern. Eingeschaltet = direkt in der Leiste, ausgeschaltet = unter „Mehr“.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
          {list.map((n, i) => (
            <Row key={n.to} n={n} i={i} last={i === list.length - 1} on={!!sel[n.to]} onMove={move}
              onToggle={(v) => setSel((s) => ({ ...s, [n.to]: v }))} />
          ))}
        </ul>
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => save(null)} data-testid="nav-settings-reset">Standard</Button>
          <Button disabled={busy} onClick={() => save({ more: list.filter((n) => !sel[n.to]).map((n) => n.to), order: list.map((n) => n.to) })}
            data-testid="nav-settings-save">Speichern</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
